/**
 * A section of the connection form (Option C, C2): General, Server / File / Cluster / Storage, Security, More options.
 * Its heading is a disclosure button (`aria-expanded`); a collapsed section keeps its fields MOUNTED — only hidden — so
 * validation, fix chips and remedy links still reach them, and any programmatic focus opens it first and focuses once
 * the opening committed (`formSectionReveal.ts`, rule 3b.2). More options starts collapsed; its heading then names what it holds.
 *
 * Analytics: `ui:connection_form_section` { section, enabled } on every user toggle, both directions (plan §3.7).
 */
import { useId, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { cn } from '@/lib/utils';
import { useRevealOnRequest } from './formSectionReveal';

/** The section kinds, as `ui:connection_form_section.section` reports them (low cardinality). */
export type FormSectionId = 'general' | 'server' | 'file' | 'cluster' | 'storage' | 'security' | 'more_options';

type Props = {
  id: FormSectionId;
  title: string;
  defaultOpen?: boolean;
  /** Shown after the title while collapsed: what the section holds (e.g. "AI access: Read-only"). */
  summary?: string;
  children: ReactNode;
};

export function FormSection({ id, title, defaultOpen = true, summary, children }: Props) {
  const { track } = useAnalytics();
  const [open, setOpen] = useState(defaultOpen);
  const sectionRef = useRef<HTMLElement>(null);
  const bodyId = useId();
  const titleId = `form-section-${id}-title`;
  // A programmatic focus / scroll opens the section and waits for the opening to commit (C2 review C2-04).
  useRevealOnRequest(sectionRef, open, () => setOpen(true));

  const toggle = () => {
    const next = !open;
    setOpen(next);
    track('ui:connection_form_section', { section: id, enabled: next });
  };

  return (
    <section ref={sectionRef} aria-labelledby={titleId} data-form-section={id} data-state={open ? 'open' : 'closed'} className="pt-1">
      <h4 className="flex items-center gap-2" id={titleId}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={toggle}
          className="-ml-1 flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`form-section-${id}-toggle`}
        >
          <ChevronRight aria-hidden className={cn('h-3 w-3 shrink-0 transition-transform duration-150 motion-reduce:transition-none', open && 'rotate-90')} />
          <span className="shrink-0">{title}</span>
          {!open && summary && <span className="min-w-0 truncate pl-1 font-normal normal-case tracking-normal" data-testid={`form-section-${id}-summary`}>{summary}</span>}
        </button>
        <span aria-hidden className="h-px min-w-4 flex-1 bg-border" />
      </h4>
      <div id={bodyId} hidden={!open} className="space-y-3 pb-2 pt-2" data-form-section-body data-testid={`form-section-${id}`}>
        {children}
      </div>
    </section>
  );
}
